import axios, { AxiosError } from "axios";
import { useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { TRADES_API_URL } from "../../../utils/env";
import { FilledTradesEndpointEnum } from "../../../enums/endpoints";

export interface ITradeRewardRecord {
  Account: string;
  Epoch: number;
  TradingVolume: number;
  PercentageShare: number;
  RewardAmount: string;
  _CreatedTimestamp: number;
}

interface ITradeRewardsHistoryResponse {
  success: boolean;
  message: string;
  data: ITradeRewardRecord[] | { data: ITradeRewardRecord[] };
}

export const useTradingRewardsHistory = () => {
  const { tokenId } = useContext(AuthContext);

  const fetchTradingRewardsHistory = async (): Promise<
    ITradeRewardRecord[] | undefined
  > => {
    if (!tokenId) {
      return undefined;
    }

    try {
      const response = await axios.get<ITradeRewardsHistoryResponse>(
        TRADES_API_URL(FilledTradesEndpointEnum.TRADE_HISTORY),
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${tokenId}`,
            "Access-Control-Allow-Origin": "*",
          },
        }
      );

      if (response.data.success && response.data.data) {
        // Handle different response structures
        if (Array.isArray(response.data.data)) {
          return response.data.data;
        }
        if (
          typeof response.data.data === "object" &&
          "data" in response.data.data
        ) {
          return (response.data.data as { data: ITradeRewardRecord[] }).data;
        }
      }

      return [];
    } catch (error) {
      // do nothing
      return undefined;
    }
  };

  const swr = useSWR<ITradeRewardRecord[] | undefined, AxiosError>(
    tokenId ? ["trade-history", tokenId] : null,
    fetchTradingRewardsHistory,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000, // Cache for 1 minute
      refreshInterval: 300000, // Refresh every 5 minutes
    }
  );

  return {
    ...swr,
  };
};
